import React from 'react';
import { useTheme } from '@mui/material/styles';
import { Grid, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell, Box } from '@mui/material';
import Chart from 'react-apexcharts';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

const ReportVisualization = ({
    loading,
    data,
    type,
    options,
    series,
    tableHeaders,
    tableData,
    highchartsOptions,
    width = '100%',
    height = 300
}) => {
    const theme = useTheme();

    const renderLoading = () => (
        <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '16px', fontWeight: 'bold' }}>Please wait...</Typography>
    );

    const renderNoData = () => (
        <Typography sx={{ textAlign: 'center', mt: 3 }} color={theme.palette.secondary.main}>
            No data available
        </Typography>
    );

    const renderSectionData = () => {
        if (!data?.sections?.values?.length) {
            return null;
        }

        return (
            <Box sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Sections
                </Typography>
                {data.sections.values.map((section, index) => (
                    <Paper key={index} sx={{ p: 2, mb: 2 }}>
                        {data.sections.attribs.map((attr, attrIndex) => (
                            <Box key={attrIndex} sx={{ mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                    {attr}:
                                </Typography>
                                <Typography variant="body2">{section[attr]}</Typography>
                            </Box>
                        ))}
                    </Paper>
                ))}
            </Box>
        );
    };

    const renderChart = () => {
        if (type === 'highcharts') {
            return (
                <div style={{ width }}>
                    <HighchartsReact highcharts={Highcharts} options={highchartsOptions} />
                </div>
            );
        }

        if (type === 'bar') {
            return (
                <div style={{ overflow: 'hidden', height: height }}>
                    <Chart
                        options={options}
                        series={series}
                        type={type}
                        height={height}
                        width={width}
                        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}
                    />
                </div>
            );
        }

        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width }}>
                <Chart options={options} series={series} type={type} width={width} height={height} />
            </div>
        );
    };

    const renderTable = () => (
        <Paper sx={{ overflowX: 'hidden' }}>
            <Table>
                <TableHead>
                    <TableRow>
                        {tableHeaders.map((header, index) => (
                            <TableCell key={index}>{header}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tableData.map((row, rowIndex) => (
                        <TableRow key={rowIndex}>
                            {row.map((cell, cellIndex) => (
                                <TableCell key={cellIndex}>{cell}</TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Paper>
    );

    if (loading) {
        return renderLoading();
    }
    if (!data || (Array.isArray(data) && data.length === 0)) {
        return renderNoData();
    }

    if (type === 'table') {
        return renderTable();
    }

    if (type === 'section') {
        return renderSectionData();
    }

    return renderChart();
};

export default ReportVisualization;
